import { z } from 'zod';
import { ACTION_COOLDOWNS, DESTINATION_TYPES, SOURCE_TYPES } from './agents.js';

// Action API DTOs (snake_case wire contract). An action is
// a user-saved instance of a SINGLE catalog action — picked from the catalog and
// runnable once or on a schedule. See packages/db/src/schema/actions.ts
// and the Actions IA plan. (Replaces the deprecated multi-agent `automations`.)

/** Scope of an action: org-wide or a set of projects. */
export const ACTION_SCOPES = ['org', 'project'] as const;
export type ActionScope = (typeof ACTION_SCOPES)[number];

/** Lifecycle: added-but-unconfigured vs ready to run. */
export const ACTION_STATUSES = ['draft', 'ready'] as const;
export type ActionStatus = (typeof ACTION_STATUSES)[number];

// ── Sources & Destinations (symmetric I/O) ────────────────────────────────
// An action reads from Sources[] and writes to Destinations[]. A `project` is
// the first source type; integrations (CRM, SEO) plug in later via `connections`.
// Destinations subsume the old "notify task" + "Then" step: `notify` is one type
// alongside `webhook`, `crm` (write-back), `internal` (e.g. ICP→profile). Stored in `actions.options.{sources,destinations}`
// for now (no migration); may promote to columns later.

export const actionSourceSchema = z.object({
  type: z.enum(SOURCE_TYPES),
  /** projectId, or a `connections` row id for integrations. */
  id: z.string().max(128),
});
export type ActionSource = z.infer<typeof actionSourceSchema>;

export const actionDestinationSchema = z.object({
  // Server-minted and stable across reorders; CRM retries find their target by it.
  // A malformed id is ignored, never a reason to drop the destination.
  id: z.string().uuid().optional().catch(undefined),
  type: z.enum(DESTINATION_TYPES),
  /**
   * Per-type config: notify → { channel, recipient/webhook, subject?, message };
   * webhook → { url, headers?, body? }; crm → crmDestinationConfigSchema;
   * internal → {}.
   */
  config: z.record(z.string(), z.unknown()).default({}),
});
export type ActionDestination = z.infer<typeof actionDestinationSchema>;

export const CRM_OBJECTS = ['company'] as const;
export const CRM_CREATE_POLICIES = ['update_only', 'create_missing'] as const;
export type CrmCreatePolicy = (typeof CRM_CREATE_POLICIES)[number];

// Write-path contract for a `crm` destination: fixed owned fields, no mappings.
export const crmDestinationConfigSchema = z
  .object({
    connection_id: z.string().uuid(),
    object: z.enum(CRM_OBJECTS),
    create_policy: z.enum(CRM_CREATE_POLICIES).default('update_only'),
    // Where the CRM supports it, also logs each signal as an activity on the record.
    log_activity: z.boolean().default(true),
  })
  .strict();
export type CrmDestinationConfig = z.infer<typeof crmDestinationConfigSchema>;

// Signal trigger (GTM Signals & Target Accounts, PRD 3.1). A signal-triggered
// action fires when a matching signal is recorded. `scope` narrows
// which accounts count: 'targets' = only Target Accounts, 'all' = any account.
// Types-only here — matching/dispatch happens in the SignalService bridge; the
// existing cron/event variants are untouched and this field is optional (fully
// backward compatible).
export const SIGNAL_TRIGGER_SCOPES = ['targets', 'all'] as const;
export type SignalTriggerScope = (typeof SIGNAL_TRIGGER_SCOPES)[number];

export const signalTriggerSchema = z.object({
  rule_id: z.string().uuid().optional(),
  type: z.string().max(64),
  scope: z.enum(SIGNAL_TRIGGER_SCOPES).optional(),
});
export type SignalTrigger = z.infer<typeof signalTriggerSchema>;

export const SCHEDULE_FREQUENCIES = [
  'daily',
  'weekly',
  'monthly',
  'quarterly',
] as const;
export type ScheduleFrequency = (typeof SCHEDULE_FREQUENCIES)[number];

// Strict so a raw `cron` (or the older `event`/`signal` keys) is rejected.
export const actionScheduleSchema = z
  .object({ frequency: z.enum(SCHEDULE_FREQUENCIES) })
  .strict();
export type ActionSchedule = z.infer<typeof actionScheduleSchema>;

// Digests send their report as the message (backend `deliverMarkdownReport`), so a written message never applies.
export const REPORT_ACTION_KEYS = [
  'activity.digest',
  'activity.digest.narrative',
  'gtm.digest',
  'enrichment.digest',
] as const;

export const isReportActionKey = (key: string): boolean =>
  (REPORT_ACTION_KEYS as readonly string[]).includes(key);

// A quarter of history is a report, not a digest, and overruns the GTM Digest's signal walk.
export const DIGEST_SCHEDULE_FREQUENCIES: readonly ScheduleFrequency[] = [
  'daily',
  'weekly',
  'monthly',
];

export const scheduleFrequenciesFor = (
  actionKey: string,
): readonly ScheduleFrequency[] =>
  isReportActionKey(actionKey)
    ? DIGEST_SCHEDULE_FREQUENCIES
    : SCHEDULE_FREQUENCIES;

const DIGEST_WINDOW_DAYS: Record<
  Exclude<ScheduleFrequency, 'monthly'>,
  number
> = {
  daily: 1,
  weekly: 7,
  quarterly: 90,
};

// A digest covers the time since its last scheduled run; one without a schedule covers a week.
// Monthly runs land on the same day each month (1 to 28), so that's the previous month's length.
export const digestWindowDays = (
  schedule: ActionSchedule | null | undefined,
  now: Date = new Date(),
): number =>
  schedule?.frequency === 'monthly'
    ? new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0),
      ).getUTCDate()
    : DIGEST_WINDOW_DAYS[schedule?.frequency ?? 'weekly'];

export const digestPeriod = (days: number): string =>
  days === 1 ? 'the last 24 hours' : `the last ${days} days`;

// What fires an action automatically, besides a schedule. Any entry matching
// fires it. Stored as a list so one action can react to several events/signals.
export const actionTriggerSchema = z.union([
  z.object({ event: z.string().min(1).max(160) }).strict(),
  z.object({ signal: signalTriggerSchema }).strict(),
]);
export type ActionTrigger = z.infer<typeof actionTriggerSchema>;

export const actionSchema = z.object({
  id: z.string(),
  name: z.string(),
  action_key: z.string(),
  status: z.enum(ACTION_STATUSES),
  scope: z.enum(ACTION_SCOPES),
  project_ids: z.array(z.string()),
  profile_id: z.string().nullable(),
  agent_overrides: z.record(z.string(), z.boolean()),
  options: z.record(z.string(), z.unknown()),
  memory_override: z.string().nullable(),
  schedule: actionScheduleSchema.nullable(),
  triggers: z.array(actionTriggerSchema),
  cooldown: z.enum(ACTION_COOLDOWNS),
  advanced: z.record(z.string(), z.unknown()),
  enabled: z.boolean(),
  // Status-table columns: next scheduled fire (schedule mode only) + the most
  // recent run, resolved from action_runs by the list endpoint.
  next_run_at: z.string().nullable(),
  last_run_at: z.string().nullable(),
  last_run_status: z.string().nullable(),
  // Why the latest run failed; null unless it did.
  last_run_error: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Action = z.infer<typeof actionSchema>;

export const actionsListResponseSchema = z.object({
  actions: z.array(actionSchema),
});
export type ActionsListResponse = z.infer<typeof actionsListResponseSchema>;

// Create: `name` + `action_key` required. `status` defaults to 'draft' (the
// "Add to My Actions" path); the "Add & Configure" path sends 'ready' with the
// rest filled in. Cross-field rules (action_key ∈ catalog, schedule/triggers
// ∈ the action's runModes, scope/project_ids coherence, profile ownership) are
// validated in the service against the live catalog.
const actionRequestFields = z.object({
  name: z.string().min(1).max(160),
  action_key: z.string().min(1).max(64),
  status: z.enum(ACTION_STATUSES),
  scope: z.enum(ACTION_SCOPES),
  project_ids: z.array(z.string().max(64)).max(200),
  profile_id: z.string().max(64).nullable().optional(),
  agent_overrides: z.record(z.string(), z.boolean()).optional(),
  options: z.record(z.string(), z.unknown()).optional(),
  memory_override: z.string().max(8000).nullable().optional(),
  schedule: actionScheduleSchema
    .nullable()
    .optional()
    .describe(
      '`{ frequency }` to run on a schedule. The run time is picked for you; see `next_run_at`.',
    ),
  cooldown: z
    .enum(ACTION_COOLDOWNS)
    .optional()
    .describe(
      "After a trigger, at most one run per company in this window; 'occurrence' runs on every match. Defaults per action type.",
    ),
  triggers: z
    .array(actionTriggerSchema)
    .max(10)
    .optional()
    .describe(
      'What runs the action besides a schedule; any match runs it. Each item is `{ "event": "<event name>" }` or `{ "signal": { "type": "<signal type>", "scope": "targets" | "all" } }`.',
    ),
  advanced: z.record(z.string(), z.unknown()).optional(),
  enabled: z.boolean().optional(),
});

export const createActionRequestSchema = actionRequestFields.extend({
  status: z.enum(ACTION_STATUSES).default('draft'),
  scope: z.enum(ACTION_SCOPES).default('org'),
  project_ids: z.array(z.string().max(64)).max(200).default([]),
});
export type CreateActionRequest = z.infer<typeof createActionRequestSchema>;
// What a client sends: the defaulted fields may be omitted.
export type CreateActionInput = z.input<typeof createActionRequestSchema>;

// No defaults here: a partial update must only write the fields it was sent.
export const patchActionRequestSchema = actionRequestFields.partial();
export type PatchActionRequest = z.infer<typeof patchActionRequestSchema>;

// Run once now. `project_id` picks which project a project-scoped action targets
// when it lists more than one (optional; defaults to the first).
export const runActionRequestSchema = z.object({
  project_id: z.string().max(64).optional(),
});
export type RunActionRequest = z.infer<typeof runActionRequestSchema>;
